import { useState, useEffect, useRef } from 'react'
import { db, realtime, supabase } from '../lib/supabase'
import { Send, MessageCircle, Users } from 'lucide-react'

/**
 * MediaChannelChat - Real-time chat for media watch parties
 *
 * A compact chat panel designed to sit alongside the media player,
 * allowing viewers to chat while watching together.
 */
export default function MediaChannelChat({ channel, user, profile }) {
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const [viewerCount, setViewerCount] = useState(1)
  const messagesEndRef = useRef(null)
  const subscriptionRef = useRef(null)
  const inputRef = useRef(null)

  // Load messages and subscribe when channel changes
  useEffect(() => {
    if (channel?.id) {
      loadMessages()
      subscribeToMessages()
    }

    return () => {
      if (subscriptionRef.current) {
        realtime.unsubscribe(subscriptionRef.current)
      }
    }
  }, [channel?.id])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const loadMessages = async () => {
    setLoading(true)
    try {
      const { data, error } = await db.messages.list(channel.id)
      if (error) throw error
      setMessages(data || [])
    } catch (error) {
      console.error('Error loading messages:', error)
    }
    setLoading(false)
  }

  const subscribeToMessages = () => {
    // Subscribe to channel_messages table for this channel
    subscriptionRef.current = supabase
      .channel(`media-chat:${channel.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'channel_messages',
          filter: `channel_id=eq.${channel.id}`,
        },
        async (payload) => {
          // Fetch the full message with profile data
          const { data } = await supabase
            .from('channel_messages')
            .select('*, profiles:user_id(*)')
            .eq('id', payload.new.id)
            .single()

          if (data) {
            setMessages((prev) => [...prev, data])
          }
        }
      )
      .subscribe()
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || isSending) return

    setIsSending(true)
    try {
      const { error } = await db.messages.send({
        user_id: user.id,
        channel_id: channel.id,
        content: newMessage.trim(),
      })

      if (error) throw error
      setNewMessage('')
      inputRef.current?.focus()
    } catch (error) {
      console.error('Error sending message:', error)
    } finally {
      setIsSending(false)
    }
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="w-80 lg:w-96 h-full flex flex-col bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-700/30 overflow-hidden">
      {/* Chat Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-700/30 bg-gray-800/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-indigo-400" />
            <h3 className="font-semibold text-white text-sm">Live Chat</h3>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Users className="w-3.5 h-3.5" />
            <span>{viewerCount} watching</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-2">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-gray-500">Loading chat...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-2 px-4">
              <MessageCircle className="w-8 h-8 text-gray-600 mx-auto" />
              <p className="text-sm text-gray-500">No messages yet</p>
              <p className="text-xs text-gray-600">Be the first to say something!</p>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              isOwn={message.user_id === user.id}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="flex-shrink-0 p-3 border-t border-gray-700/30 bg-gray-800/40">
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Send a message..."
            className="flex-1 px-3 py-2 bg-gray-800/60 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-colors"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={isSending || !newMessage.trim()}
            className="p-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

/**
 * ChatMessage - Individual message component
 * Compact design for media channel chat
 */
function ChatMessage({ message, isOwn }) {
  const sender = message.profiles
  const username = sender?.username || 'Anonymous'
  const avatarUrl = sender?.avatar_url

  // Generate a consistent color for the username based on user_id
  const getNameColor = (userId) => {
    const colors = [
      'text-red-400',
      'text-orange-400',
      'text-amber-400',
      'text-yellow-400',
      'text-lime-400',
      'text-green-400',
      'text-emerald-400',
      'text-teal-400',
      'text-cyan-400',
      'text-sky-400',
      'text-blue-400',
      'text-indigo-400',
      'text-violet-400',
      'text-purple-400',
      'text-fuchsia-400',
      'text-pink-400',
      'text-rose-400',
    ]
    // Simple hash to get consistent color
    let hash = 0
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  const nameColor = isOwn ? 'text-indigo-400' : getNameColor(message.user_id)

  return (
    <div className="group flex items-start gap-2 py-1 px-2 rounded-lg hover:bg-gray-800/40 transition-colors">
      {/* Avatar */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full overflow-hidden bg-gray-700">
        {avatarUrl ? (
          <img src={avatarUrl} alt={username} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-400">
            {username.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={`text-xs font-semibold ${nameColor} truncate max-w-[100px]`}>
            {username}
          </span>
          <span className="text-[10px] text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        <p className="text-sm text-gray-300 break-words leading-snug">
          {message.content}
        </p>
      </div>
    </div>
  )
}
